// src/hooks/useRoleTesting.js
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export const useRoleTesting = () => {
  const { user } = useAuth();
  const [testingMode, setTestingMode] = useState(false);
  const [testRole, setTestRole] = useState(null);

  // Solo activar en desarrollo por defecto
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setTestingMode(true);
    }
  }, []);

  // Obtener el rol actual (real o de testing)
  const getCurrentRole = () => {
    if (testingMode && testRole) {
      return testRole;
    }
    return user?.rol || user?.role || 'atleta';
  };

  // Cambiar rol de testing
  const setTestingRole = (newRole) => {
    if (testingMode) {
      setTestRole(newRole);
      // Guardar en localStorage para persistir entre recargas
      localStorage.setItem('testingRole', newRole);
    }
  };

  // Obtener rol de testing desde localStorage al cargar
  useEffect(() => {
    if (testingMode) {
      const savedTestRole = localStorage.getItem('testingRole');
      if (savedTestRole) {
        setTestRole(savedTestRole);
      }
    }
  }, [testingMode]);

  // Resetear a rol real
  const resetToRealRole = () => {
    setTestRole(null);
    localStorage.removeItem('testingRole');
  };

  // Activar/desactivar modo testing
  const toggleTestingMode = () => {
    const newTestingMode = !testingMode;
    setTestingMode(newTestingMode);
    
    if (!newTestingMode) {
      resetToRealRole();
    }
  };

  // Obtener usuario mock con rol de testing
  const getTestUser = () => {
    if (!testingMode || !testRole) {
      return user;
    }

    // Crear usuario mock con el rol de testing
    const mockUsers = {
      admin: {
        ...user,
        rol: 'admin',
        role: 'admin',
        displayName: 'Admin Demo',
        email: 'admin@demo.com'
      },
      organizador: {
        ...user,
        rol: 'organizador', 
        role: 'organizador',
        displayName: 'María Organizadora',
        email: 'organizador@demo.com'
      },
      cronometrador: {
        ...user,
        rol: 'cronometrador',
        role: 'cronometrador', 
        displayName: 'Carlos Cronometrador',
        email: 'cronometrador@demo.com'
      },
      entrenador: {
        ...user,
        rol: 'entrenador',
        role: 'entrenador',
        displayName: 'Ana Entrenadora', 
        email: 'entrenador@demo.com'
      },
      atleta: {
        ...user,
        rol: 'atleta',
        role: 'atleta',
        displayName: 'Juan Atleta',
        email: 'atleta@demo.com'
      },
      juez: {
        ...user,
        rol: 'juez',
        role: 'juez', 
        displayName: 'Roberto Juez',
        email: 'juez@demo.com'
      },
      transmision: {
        ...user,
        rol: 'transmision',
        role: 'transmision',
        displayName: 'Laura Transmisión',
        email: 'transmision@demo.com'
      },
      locutor: {
        ...user,
        rol: 'locutor',
        role: 'locutor',
        displayName: 'Pedro Locutor', 
        email: 'locutor@demo.com'
      },
      prensa: {
        ...user,
        rol: 'prensa',
        role: 'prensa',
        displayName: 'Carmen Periodista',
        email: 'prensa@demo.com'
      },
      fotografo: {
        ...user,
        rol: 'fotografo',
        role: 'fotografo', 
        displayName: 'Miguel Fotógrafo',
        email: 'fotografo@demo.com'
      }
    };

    return mockUsers[testRole] || user;
  };

  return {
    // Estado
    testingMode,
    isUsingTestRole: testingMode && testRole !== null,
    currentRole: getCurrentRole(),
    testRole,
    realRole: user?.rol || user?.role || 'atleta',
    
    // Usuario (real o mock)
    user: getTestUser(),
    
    // Acciones
    setTestingRole,
    resetToRealRole,
    toggleTestingMode,
    
    // Información
    availableRoles: [
      'admin', 'organizador', 'cronometrador', 'entrenador', 
      'atleta', 'juez', 'transmision', 'locutor', 'prensa', 'fotografo'
    ]
  };
};

export default useRoleTesting;